import type { LocationQuery, LocationQueryValue } from 'vue-router'

/**
 * Vue Router 4 (Vue 3) Route Query Helper
 * 
 * Vue Router 4 changed query param types to be more strict:
 * - Query values are now `string | null | (string | null)[]`
 * - This helper provides type-safe access to query parameters
 */

/**
 * Get a single query parameter as string
 * Handles both single values and arrays (returns first element if array)
 * 
 * @param query - The route query object ($route.query)
 * @param key - The query parameter key
 * @param defaultValue - Optional default value if param is null/undefined
 * @returns The query parameter value as string or null
 * 
 * @example
 * // In component:
 * import { getQueryString } from '@/helpers/route-helper'
 * const lessonId = getQueryString(this.$route.query, 'lesson_id')
 * const tab = getQueryString(this.$route.query, 'tab', '0')
 */
export function getQueryString(
  query: LocationQuery,
  key: string,
  defaultValue: string | null = null
): string | null {
  const value = query[key]
  if (value === undefined || value === null) {
    return defaultValue
  }
  if (Array.isArray(value)) {
    return value[0] ?? defaultValue
  }
  return value
}

/**
 * Get a query parameter as number
 * Returns null if the value is not a valid number
 * 
 * @param query - The route query object ($route.query)
 * @param key - The query parameter key
 * @param defaultValue - Optional default value if param is null/undefined or not a number
 * @returns The query parameter value as number or null
 * 
 * @example
 * const page = getQueryNumber(this.$route.query, 'page', 1)
 */
export function getQueryNumber(
  query: LocationQuery,
  key: string,
  defaultValue: number | null = null
): number | null {
  const value = getQueryString(query, key)
  if (value === null) {
    return defaultValue
  }
  const parsed = Number(value)
  return isNaN(parsed) ? defaultValue : parsed
}

/**
 * Get a query parameter as boolean
 * Returns true for 'true', '1', 'yes'; false for 'false', '0', 'no'; null otherwise
 * 
 * @param query - The route query object ($route.query)
 * @param key - The query parameter key
 * @param defaultValue - Optional default value
 * @returns The query parameter value as boolean or null
 * 
 * @example
 * const isPreview = getQueryBoolean(this.$route.query, 'preview', false)
 */
export function getQueryBoolean(
  query: LocationQuery,
  key: string,
  defaultValue: boolean | null = null
): boolean | null {
  const value = getQueryString(query, key)
  if (value === null) {
    return defaultValue
  }
  const truthyValues = ['true', '1', 'yes']
  const falsyValues = ['false', '0', 'no']
  const lowered = value.toLowerCase()

  if (truthyValues.includes(lowered)) return true
  if (falsyValues.includes(lowered)) return false
  return defaultValue
}

/**
 * Get all values for a query parameter as string array
 * Useful for multi-select filters
 * 
 * @param query - The route query object ($route.query)
 * @param key - The query parameter key
 * @returns Array of string values
 * 
 * @example
 * const selectedTags = getQueryArray(this.$route.query, 'tags')
 */
export function getQueryArray(
  query: LocationQuery,
  key: string
): string[] {
  const value = query[key]
  if (value === undefined || value === null) {
    return []
  }
  if (Array.isArray(value)) {
    return value.filter((v): v is string => v !== null)
  }
  return [value]
}

/**
 * Check if a query parameter exists and has a non-empty value
 * 
 * @param query - The route query object ($route.query)
 * @param key - The query parameter key
 * @returns True if the parameter exists and is not empty
 * 
 * @example
 * if (hasQueryParam(this.$route.query, 'filter')) { ... }
 */
export function hasQueryParam(
  query: LocationQuery,
  key: string
): boolean {
  const value = getQueryString(query, key)
  return value !== null && value !== ''
}

/**
 * Type-safe query value extractor (inline version)
 * Use when you just need a quick string extraction
 * 
 * @param value - The LocationQueryValue from $route.query[key]
 * @returns string or null
 * 
 * @example
 * const id = queryToString(this.$route.query.lesson_id)
 */
export function queryToString(
  value: LocationQueryValue | LocationQueryValue[]
): string | null {
  if (value === undefined || value === null) {
    return null
  }
  if (Array.isArray(value)) {
    return value[0] ?? null
  }
  return value
}
